// frontend/src/pages/ListaPartesDiarios.jsx
// LISTA DE PARTES DIARIOS USANDO COMPONENTE UNIFICADO
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ListaUnificada from '../components/common/ListaUnificada.jsx';
import { ModuloHeader, ModuloShell } from '../components/common/ModuloHeader.jsx';
import ModalParteDiario from '../components/formularios/ModalParteDiario.jsx';
import { LinkEquipo } from '../components/common/Links.jsx';
import apiClient from '../services/apiClient';
import { useToast } from '../components/common/Toast';
import { useConfirm } from '../components/common/ModalConfirmacion';

const HELP = {
  tips: [
    'El parte diario es la base de todo el sistema: de acá salen las horas trabajadas y las lecturas de contador.',
    'Sin partes cargados, los mantenimientos no vencen a tiempo y los informes quedan incompletos.',
    'La lectura del horómetro o cuentakilómetros debe ser siempre mayor a la del parte anterior del mismo equipo.',
    'Los operadores pueden cargar su parte desde el celular escaneando el código del equipo.',
  ],
  nextSteps: [
    'Revisar el informe de partes diarios para detectar equipos sin carga.',
    'Con las lecturas al día, la gestión de mantenimientos calcula sola los próximos vencimientos.',
  ],
};

const ListaPartesDiarios = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirmar } = useConfirm();

  // Estados para formularios React
  const [showModal, setShowModal] = useState(false);
  const [editingParteDiario, setEditingParteDiario] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  
  // Ref para acceder directamente a ListaUnificada y forzar refresco
  const listaUnificadaRef = useRef();

  useEffect(() => {
    const ctx = location.state?.returnContext;
    if (!ctx || ctx.sourcePath !== '/partes' || ctx.sourceField !== 'equipo') return;

    const openReturnContext = async () => {
      try {
        if (ctx.sourceRecordId) {
          const resp = await apiClient.get(`partes-diarios/${ctx.sourceRecordId}/`);
          setEditingParteDiario(resp.data || null);
        } else {
          setEditingParteDiario(ctx.sourceFormData || null);
        }
        setShowModal(true);
      } catch (err) {
        console.warn('No se pudo reabrir el parte al volver desde equipo:', err);
        setEditingParteDiario(ctx.sourceFormData || null);
        setShowModal(true);
      } finally {
        // Limpiar state de navegación para evitar reapertura en refresh/back
        navigate(location.pathname + location.search, { replace: true, state: null });
      }
    };

    openReturnContext();
  }, [location.pathname, location.search, location.state, navigate]);

  // ============================= CONFIGURACIÓN DE COLUMNAS =============================
  // Configuración de columnas basada en el modelo ParteDiario REAL
  const columns = [
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      sortKey: 'equipo__interno', // Para ordenamiento en backend
      width: '8%',
      render: (value, row) => (
        <LinkEquipo
          href={`/?equipoId=${row.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${row.equipo}`;
          }}
        >
          {value}
        </LinkEquipo>
      )
    },
    {
      key: 'equipo_descripcion',
      label: 'Descripción',
      sortable: true,
      sortKey: 'equipo__descripcion',
      width: '10%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_marca_nombre',
      label: 'Marca',
      sortable: true,
      sortKey: 'equipo__marca__nombre',
      width: '7%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_modelo_nombre',
      label: 'Modelo',
      sortable: true,
      sortKey: 'equipo__modelo__nombre',
      width: '7%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_familia_nombre',
      label: 'Familia',
      sortable: true,
      sortKey: 'equipo__familia__nombre',
      width: '7%'
    },
    {
      key: 'fecha',
      label: 'Fecha',
      sortable: true,
      sortKey: 'fecha',
      width: '8%',
      render: (value) => require('../utils/dateUtils').formatDateDisplay(value)
    },
    {
      key: 'operador',
      label: 'Operador',
      sortable: true,
      sortKey: 'operador',
      width: '12%'
    },
    { 
      key: 'centro_costo_nombre', 
      label: 'Centro de Costo', 
      sortable: true,
      sortKey: 'centro_costo__nombre',
      width: '12%'
    },
    {
      key: 'obra_nombre',
      label: 'Obra',
      sortable: true,
      sortKey: 'obra__nombre',
      width: '12%',
      render: (value) => value || '-'
    },
    {
      key: 'sector_nombre',
      label: 'Sector',
      sortable: true,
      sortKey: 'sector__nombre',
      width: '10%',
      render: (value) => value || '-'
    },
    { 
      key: 'kilometros', 
      label: 'Kilómetros', 
      sortable: true,
      sortKey: 'kilometros',
      width: '8%',
      render: (value) => value ? `${value} km` : '-'
    },
    { 
      key: 'horas', 
      label: 'Horas', 
      sortable: true,
      sortKey: 'horas',
      width: '8%',
      render: (value) => value ? `${value} hs` : '-'
    },
    {
      key: 'tareas',
      label: 'Tareas',
      sortable: false,
      width: '11%',
      render: (value) => {
        if (!value) return '-';
        return value.length > 30 ? `${value.substring(0, 30)}...` : value;
      }
    },
    {
      key: 'observaciones',
      label: 'Observaciones',
      sortable: false,
      width: '10%',
      render: (value) => {
        if (!value) return '-';
        return value.length > 30 ? `${value.substring(0, 30)}...` : value;
      }
    }
  ];

  // ============================= CONFIGURACIÓN DE FILTROS =============================
  const filterConfig = [
    {
      key: 'equipo__interno__icontains',
      label: 'Equipo',
      type: 'text',
      placeholder: 'Filtrar por equipo...'
    },
    {
      key: 'equipo__descripcion__icontains',
      label: 'Descripción',
      type: 'text',
      sourceKey: 'equipo_descripcion',
      placeholder: 'Filtrar por descripción...'
    },
    {
      key: 'equipo__marca__nombre__iexact',
      label: 'Marca',
      type: 'text',
      sourceKey: 'equipo_marca_nombre',
      placeholder: 'Filtrar por marca...'
    },
    {
      key: 'equipo__modelo__nombre__iexact',
      label: 'Modelo',
      type: 'text',
      sourceKey: 'equipo_modelo_nombre',
      placeholder: 'Filtrar por modelo...'
    },
    {
      key: 'fecha__gte',
      label: 'Fecha desde',
      type: 'date',
      placeholder: ''
    },
    {
      key: 'fecha__lte',
      label: 'Fecha hasta',
      type: 'date',
      placeholder: ''
    },
    {
      key: 'operador__icontains',
      label: 'Operador',
      type: 'text',
      placeholder: 'Filtrar por operador...'
    },
    {
      key: 'equipo__familia__nombre__iexact',
      label: 'Familia',
      type: 'text',
      sourceKey: 'equipo_familia_nombre',
      placeholder: 'Filtrar por familia...'
    },
    {
      key: 'equipo__tipo__nombre__iexact',
      label: 'Tipo',
      type: 'text',
      sourceKey: 'equipo_tipo_nombre',
      placeholder: 'Filtrar por tipo...'
    },
    {
      key: 'centro_costo__nombre__icontains',
      label: 'Centro de Costo',
      type: 'text',
      sourceKey: 'centro_costo_nombre',
      placeholder: 'Filtrar por centro de costo...'
    },
    {
      key: 'obra__nombre__icontains',
      label: 'Obra',
      type: 'text',
      sourceKey: 'obra_nombre',
      placeholder: 'Filtrar por obra...'
    },
    {
      key: 'sector__nombre__icontains',
      label: 'Sector',
      type: 'text',
      placeholder: 'Filtrar por sector...'
    }
  ];

  // ============================= MANEJADORES DE EVENTOS =============================

  const handleAdd = () => {
    console.log('➕ Agregar parte diario');
    setEditingParteDiario(null);
    setShowModal(true);
  };

  const handleEdit = (parteDiario) => {
    console.log('✏️ Editar parte diario:', parteDiario);
    setEditingParteDiario(parteDiario);
    setShowModal(true);
  };

  const handleFormSave = async (data) => {
    try {
      // Usar la referencia para refrescar la lista directamente
      if (listaUnificadaRef.current) {
        listaUnificadaRef.current.refreshImmediate();
      }
      
      // Retornar true para indicar que manejamos el cierre
      return true;
      
    } catch (error) {
      console.error('❌ Error guardando parte diario:', error);
    }
  };

  const handleFormCancel = () => {
    console.log('❌ Cancelar formulario');
    setShowModal(false);
    setEditingParteDiario(null);
  };

  const handleDelete = async (parteDiario) => {
    const ok = await confirmar({
      titulo: '¿Eliminar parte diario?',
      mensaje: `Se eliminará el parte diario del ${require('../utils/dateUtils').formatDateDisplay(parteDiario.fecha)}.`,
      labelConfirmar: 'Sí, eliminar',
      variante: 'danger',
    });
    if (!ok) return;

    try {
      console.log('🗑️ Eliminando parte diario:', parteDiario);
      // Aquí iría la lógica de eliminación
    } catch (error) {
      console.error('❌ Error eliminando parte diario:', error);
      toast.error('Error al eliminar el parte diario');
    }
  };

  // ============================= RENDERIZADO =============================

  return (
    <ModuloShell>
      <ModuloHeader
        icono="bi bi-journal-text"
        titulo="Partes"
        descripcion="Partes diarios de los equipos: horas trabajadas y lecturas de contador."
        help={HELP}
        helpOpen={helpOpen}
        onToggleHelp={() => setHelpOpen(v => !v)}
      />

      <ListaUnificada
        ref={listaUnificadaRef}
        cloudScope="lista-partes-diarios.state.v1"
        titulo="Partes Diarios"
        barraSeccion
        textoBotonAgregar="Nuevo parte"
        style={{ flex: 1, minHeight: 0 }}
        endpoint="partes-diarios/"
        useAdvancedFilters={true}
        tableVisualVariant="rows"
        columns={columns}
        filterConfig={filterConfig}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar partes diarios..."
        itemsPerPageOptions={[10, 25, 50, 100]}
        defaultItemsPerPage={25}
        allowAdd={true}
        allowEdit={true}
        allowDelete={true}
      />

      {/* Modal del formulario */}
      {showModal && (
        <ModalParteDiario
          isOpen={showModal}
          onClose={handleFormCancel}
          parteDiario={editingParteDiario}
          onSaved={handleFormSave}
        />
      )}
    </ModuloShell>
  );
};

export default ListaPartesDiarios;
